import React from 'react';

import Text from '~components/atoms/Text';
import CustomSkeleton from '~components/atoms/Skeleton';

import { useAppSelector } from '~redux/hooks';
import { FundTypeEnum, GoldTypeEnum } from '~utils/enums';

import * as Styles from './_preciousMetalTable.module.scss';
import { TDashboardProductInventory } from '~types/feat/dashboard';

const PreciousMetalTable = () => {
    const dashboardGoldProductInventory = useAppSelector(state => state.dashboard.dashboardGoldProductInventory);
    const loading = useAppSelector(state => state.dashboard.loadings.getDashboardProductInventory);
    const error = useAppSelector(state => state.dashboard.errors.getDashboardProductInventory);

    const filters = useAppSelector(state => state.dashboard.dashboardGoldProductInventoryFilters);

    const tableHeader = [
        'Denomination',
        'Quantity (pcs)',
        'Total Weight (gram)',
    ];

    const physicalGoldInventory = dashboardGoldProductInventory.filter(product => product.fundType === FundTypeEnum.Conventional);

    const massagedInventory = physicalGoldInventory.reduce<Map<number, TDashboardProductInventory>>((acc, product) => {
        const key = product.productId;
        if (acc.has(key)) {
            const existingProduct = acc.get(key)!;
            existingProduct.amount += product.amount;
            existingProduct.totalGoldWeight += product.totalGoldWeight;
            existingProduct.paperGoldWeight += product.paperGoldWeight;
        } else {
            acc.set(key, { ...product });
        }
        return acc;
    }, new Map());

    // Convert the Map back to an array
    const combinedInventory: TDashboardProductInventory[] = Array.from(massagedInventory.values());

    const renderTableHeader = () => {
        return tableHeader.map((header, index) => {
            return (
                <th key={index}>
                    {header}
                </th>
            );
        });
    };

    const getProductGoldWeight = (product: TDashboardProductInventory) => {
        if (product.totalGoldWeight) {
            return typeof product.totalGoldWeight === 'number' ? product.totalGoldWeight.toFixed(3) : '-';
        }

        return typeof product.paperGoldWeight === 'number' ? product.paperGoldWeight.toFixed(3) : '-';
    };

    const renderTableBody = () => {
        if (loading) {
            return (
                <tr>
                    <td colSpan={3}>
                        <CustomSkeleton height={40} />
                    </td>
                </tr>
            );
        }

        if (error) {
            return (
                <tr>
                    <td colSpan={3}>
                        <Text
                            label={error}
                            isError
                        />
                    </td>
                </tr>
            );
        }

        if (!physicalGoldInventory.length) {
            return (
                <tr>
                    <td colSpan={3}>
                        <Text
                            label='No data found'
                            isError
                        />
                    </td>
                </tr>
            );
        }

        if (filters.branchCode === '') {
            return combinedInventory.sort((a, b) => a.productId - b.productId).map((product, index) => {
                return (
                    <tr key={index}>
                        <td>{product.productName}</td>
                        <td>{product.amount}</td>
                        <td>{getProductGoldWeight(product)}</td>
                    </tr>
                );
            });
        }

        return physicalGoldInventory.sort((a, b) => a.productId - b.productId).map((product, index) => {
            return (
                <tr key={index}>
                    <td>{product.productName}</td>
                    <td>{product.amount}</td>
                    <td>{getProductGoldWeight(product)}</td>
                </tr>
            );
        });
    };

    return (
        <div className={Styles.container}>
            <Text
                label='Precious Metal'
                className={Styles.title}
                isBold
            />

            <table className={Styles['table-container']}>
                <thead>
                    <tr>
                        {renderTableHeader()}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td colSpan={3} />
                    </tr>
                    {renderTableBody()}
                </tbody>
            </table>
        </div>
    );
};

export default PreciousMetalTable;
